// ─────────────────────────────────────────────────────────────────
// Glass-i — Dispatch Plans (Feature 2). Plan dispatches per the delivery
// commitment: pick a delivery window → auto-load committed work orders → choose
// which to include → create a plan. Feature-3 readiness lives on the detail page.
// ─────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Card, Table, Input, Select, Tag, Typography, Button, Space, message,
  Modal, Form, DatePicker, Alert, Popconfirm, Tooltip } from 'antd';
import { ReloadOutlined, TruckOutlined, PlusOutlined, DeleteOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

import { dispatchPlanApi } from './dispatchPlanApi';
import { PERM, hasPerm, planStatusTag, fmtWindow, fmtDate } from './dispatchPlanHelpers';
import useAuth from '../../../hooks/useAuth';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const errMsg = (e, fb) => e?.response?.data?.error?.message || e?.message || fb;

// ── New-plan modal: window → suggest committed WOs → pick → create ──
function NewPlanModal({ open, onClose, onDone }) {
  const [form] = Form.useForm();
  const [range, setRange] = useState(null);
  const [suggesting, setSuggesting] = useState(false);
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    form.resetFields(); setRange(null); setRows([]); setSelected([]);
  }, [open, form]);

  const loadSuggest = useCallback(async () => {
    if (!range || !range[0] || !range[1]) { message.info('Pick a delivery date window first.'); return; }
    setSuggesting(true);
    try {
      const data = await dispatchPlanApi.suggest({
        deliveryDateFrom: range[0].format('YYYY-MM-DD'), deliveryDateTo: range[1].format('YYYY-MM-DD')
      });
      const list = (data && data.rows) || [];
      setRows(list);
      // Pre-select the ones that still have pieces left to dispatch.
      setSelected(list.filter((r) => (r.remainingQty ?? r.committedQty) > 0).map((r) => r.workOrderId));
      if (!list.length) message.info('No work orders are committed to that window.');
    } catch (e) { message.error(errMsg(e, 'Could not load committed work orders.')); }
    finally { setSuggesting(false); }
  }, [range]);

  const submit = async () => {
    let vals;
    try { vals = await form.validateFields(); } catch { return; }
    if (!selected.length) { message.error('Select at least one work order for the plan.'); return; }
    setSaving(true);
    try {
      const out = await dispatchPlanApi.create({
        planName: vals.planName || undefined,
        deliveryDateFrom: range[0].format('YYYY-MM-DD'),
        deliveryDateTo: range[1].format('YYYY-MM-DD'),
        remarks: vals.remarks || undefined,
        workOrderIds: selected
      });
      message.success(`${out.planNo} created — ${out.itemsAdded} work order${out.itemsAdded === 1 ? '' : 's'} planned.`);
      onClose(); onDone && onDone(out.planId);
    } catch (e) { message.error(errMsg(e, 'Could not create the plan.')); }
    finally { setSaving(false); }
  };

  const columns = [
    { title: 'Work order', dataIndex: 'woNo', width: 140, fixed: 'left', render: (v) => <code className="erp-grid__code">{v}</code> },
    { title: 'Customer', dataIndex: 'customerName', width: 200, ellipsis: true, render: (v) => v || <Text type="secondary">—</Text> },
    { title: 'Delivery', dataIndex: 'deliveryDate', width: 120, render: (v) => fmtDate(v) },
    { title: 'Ordered', dataIndex: 'committedQty', width: 84, align: 'right', render: (v) => <span className="tabular-nums">{v}</span> },
    { title: 'Remaining', dataIndex: 'remainingQty', width: 96, align: 'right',
      render: (v, r) => (r.fullyDispatched ? <Tag>Fully sent</Tag> : <span className="tabular-nums">{v ?? r.committedQty}</span>) },
    { title: 'Status', dataIndex: 'status', width: 116, render: (v) => <Tag>{v}</Tag> },
    { title: '', dataIndex: 'alreadyPlanned', width: 96,
      render: (v) => (v ? <Tag color="gold">In a plan</Tag> : null) }
  ];

  const remainingPieces = useMemo(
    () => rows.filter((r) => selected.includes(r.workOrderId)).reduce((a, r) => a + (r.remainingQty ?? r.committedQty ?? 0), 0),
    [rows, selected]);

  return (
    <Modal open={open} onCancel={onClose} onOk={submit} okText="Create plan" confirmLoading={saving}
      okButtonProps={{ disabled: !selected.length }} title="New dispatch plan" destroyOnClose maskClosable={false} width={920}>
      <Alert type="info" showIcon className="mb-3" message="Plan as per delivery commitment"
        description="Pick a delivery-date window to load the committed work orders, choose which to include, and create the plan. You set the pieces to dispatch per line on the plan itself." />
      <Form form={form} layout="vertical" requiredMark="optional">
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <Form.Item name="planName" label="Plan name" style={{ flex: '1 1 260px', marginBottom: 12 }}>
            <Input placeholder="e.g. Week 34 — North route" maxLength={160} allowClear />
          </Form.Item>
          <Form.Item label="Delivery window" required style={{ flex: '1 1 320px', marginBottom: 12 }}>
            <Space.Compact style={{ width: '100%' }}>
              <RangePicker value={range} onChange={setRange} format="DD-MMM-YYYY" style={{ width: '100%' }} allowClear />
              <Tooltip title="Load committed work orders">
                <Button type="primary" icon={<ThunderboltOutlined />} loading={suggesting} onClick={loadSuggest}>Load</Button>
              </Tooltip>
            </Space.Compact>
          </Form.Item>
        </div>

        <div className="mb-1" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <Text strong style={{ fontSize: 13 }}>Committed work orders</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {rows.length ? `${selected.length} of ${rows.length} selected · ${remainingPieces} pieces to plan` : ' '}
          </Text>
        </div>
        <Table rowKey="workOrderId" size="small" columns={columns} dataSource={rows} pagination={false}
          scroll={{ x: 'max-content', y: 300 }} locale={{ emptyText: 'Pick a window and click Load' }}
          rowSelection={{ selectedRowKeys: selected, onChange: setSelected }} />

        <Form.Item name="remarks" label="Remarks" className="mt-3 mb-0">
          <Input.TextArea rows={2} maxLength={1000} placeholder="Optional note for this plan" showCount />
        </Form.Item>
      </Form>
    </Modal>
  );
}

export default function DispatchPlansPage() {
  const navigate = useNavigate();
  const { me } = useAuth();
  const canCreate = hasPerm(me, PERM.CREATE);
  const canDelete = hasPerm(me, PERM.DELETE);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [status, setStatus] = useState(undefined);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const timer = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await dispatchPlanApi.list({ page, pageSize, status: status || undefined, search: search || undefined });
      setRows((res && res.rows) || []); setTotal((res && res.total) || 0);
    } catch (e) { message.error(errMsg(e, 'Failed to load dispatch plans.')); }
    finally { setLoading(false); }
  }, [page, pageSize, status, search]);
  useEffect(() => { load(); }, [load]);

  const onSearch = useCallback((val) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => { setPage(1); setSearch(val); }, 300);
  }, []);

  const removePlan = useCallback(async (id) => {
    try { await dispatchPlanApi.remove(id); message.success('Plan deleted.'); load(); }
    catch (e) { message.error(errMsg(e, 'Delete failed.')); }
  }, [load]);

  const columns = useMemo(() => ([
    { title: 'Plan no', dataIndex: 'planNo', width: 170, render: (v, r) => (
      <Button type="link" style={{ padding: 0, height: 'auto', fontWeight: 600 }} onClick={() => navigate(`/erp/dispatch/plans/${r.id}`)}>{v}</Button>
    ) },
    { title: 'Name', dataIndex: 'planName', render: (v) => v || <Text type="secondary">—</Text> },
    { title: 'Delivery window', key: 'win', width: 220, render: (_, r) => fmtWindow(r.deliveryDateFrom, r.deliveryDateTo) },
    { title: 'Customer', dataIndex: 'customerName', width: 180, render: (v) => v || <Text type="secondary">All</Text> },
    { title: 'WOs', dataIndex: 'itemsCount', width: 70, align: 'right' },
    { title: 'Status', dataIndex: 'status', width: 120, render: (v) => { const t = planStatusTag(v); return <Tag color={t.color}>{t.label}</Tag>; } },
    { title: 'Created', dataIndex: 'createdAt', width: 130, render: (v) => fmtDate(v) },
    { title: '', key: 'act', width: 90, align: 'right',
      render: (_, r) => (canDelete ? (
        <Popconfirm title="Delete this plan?" okText="Delete" okButtonProps={{ danger: true }} onConfirm={() => removePlan(r.id)}>
          <Button size="small" type="text" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      ) : null) }
  ]), [navigate, canDelete, removePlan]);

  return (
    <div className="p-4">
      <div className="mb-3">
        <Title level={4} style={{ margin: 0 }}><TruckOutlined style={{ marginRight: 8 }} />Dispatch Plans</Title>
        <Text type="secondary">Plan dispatches by delivery commitment and track how much of each order is ready to go.</Text>
      </div>
      <Card size="small" bodyStyle={{ paddingTop: 8 }}>
        <div className="flex items-center justify-between mb-2">
          <Space>
            <Input.Search allowClear placeholder="Search plan / customer" style={{ width: 240 }} onChange={(e) => onSearch(e.target.value)} />
            <Select allowClear placeholder="All statuses" style={{ width: 160 }} value={status}
              onChange={(v) => { setStatus(v || undefined); setPage(1); }}
              options={[{ value: 'DRAFT', label: 'Draft' }, { value: 'CONFIRMED', label: 'Confirmed' }, { value: 'CANCELLED', label: 'Cancelled' }]} />
          </Space>
          <Space>
            {canCreate ? <Button type="primary" icon={<PlusOutlined />} onClick={() => setNewOpen(true)}>New plan</Button> : null}
            <Button icon={<ReloadOutlined />} onClick={load} />
          </Space>
        </div>
        <Table rowKey="id" size="small" loading={loading} columns={columns} dataSource={rows} sticky scroll={{ x: 'max-content' }}
          pagination={{ current: page, pageSize, total, showSizeChanger: true, size: 'small', pageSizeOptions: ['25', '50', '100'],
            onChange: (p, ps) => { if (ps !== pageSize) { setPageSize(ps); setPage(1); } else { setPage(p); } },
            showTotal: (t) => `${t} plan${t === 1 ? '' : 's'}` }}
          locale={{ emptyText: loading ? ' ' : 'No dispatch plans yet — click “New plan” to plan by delivery commitment.' }} />
      </Card>
      <NewPlanModal open={newOpen} onClose={() => setNewOpen(false)} onDone={(id) => { load(); if (id) navigate(`/erp/dispatch/plans/${id}`); }} />
    </div>
  );
}
